import { supabaseDataProvider } from "ra-supabase-core";
import {
  withLifecycleCallbacks,
  type DataProvider,
  type GetListParams,
  type Identifier,
  type ResourceCallbacks,
} from "ra-core";
import type {
  ContactNote,
  Deal,
  DealNote,
  RAFile,
  Sale,
  SalesFormData,
  SignUpData,
} from "../../types";
import { getActivityLog } from "../commons/activity";
import { getIsInitialized } from "./authProvider";
import { supabase } from "./supabase";

if (import.meta.env.VITE_SUPABASE_URL === undefined) {
  throw new Error("Please set the VITE_SUPABASE_URL environment variable");
}
if (import.meta.env.VITE_SB_PUBLISHABLE_KEY === undefined) {
  throw new Error(
    "Please set the VITE_SB_PUBLISHABLE_KEY environment variable",
  );
}

const baseDataProvider = supabaseDataProvider({
  instanceUrl: import.meta.env.VITE_SUPABASE_URL,
  apiKey: import.meta.env.VITE_SB_PUBLISHABLE_KEY,
  supabaseClient: supabase,
  sortOrder: "asc,desc.nullslast" as any,
});

const processCompanyLogo = async (params: any) => {
  const logo = params.data.logo;

  if (logo?.rawFile instanceof File) {
    await uploadToBucket(logo);
  }

  return {
    ...params,
    data: {
      ...params.data,
      logo,
    },
  };
};

const dataProviderWithCustomMethods = {
  ...baseDataProvider,
  async getList(resource: string, params: GetListParams) {
    if (resource === "companies") {
      return baseDataProvider.getList("companies_summary", params);
    }
    if (resource === "contacts") {
      return baseDataProvider.getList("contacts_summary", params);
    }

    return baseDataProvider.getList(resource, params);
  },
  async getOne(resource: string, params: any) {
    if (resource === "companies") {
      return baseDataProvider.getOne("companies_summary", params);
    }
    if (resource === "contacts") {
      return baseDataProvider.getOne("contacts_summary", params);
    }

    return baseDataProvider.getOne(resource, params);
  },

  async signUp({ email, password, first_name, last_name }: SignUpData) {
    const response = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          first_name,
          last_name,
        },
      },
    });

    if (!response.data?.user || response.error) {
      console.error("signUp.error", response.error);
      throw new Error(response?.error?.message || "Failed to create account");
    }

    // Update the is initialized cache
    getIsInitialized._is_initialized_cache = true;

    return {
      id: response.data.user.id,
      email,
      password,
    };
  },
  async salesCreate(body: SalesFormData) {
    const { data, error } = await supabase.functions.invoke<{ data: Sale }>(
      "users",
      {
        method: "POST",
        body,
      },
    );

    if (!data || error) {
      console.error("salesCreate.error", error);
      const errorDetails = await (async () => {
        try {
          return (await error?.context?.json()) ?? {};
        } catch {
          return {};
        }
      })();
      throw new Error(errorDetails?.message || "Failed to create the user");
    }

    return data.data;
  },
  async salesUpdate(
    id: Identifier,
    data: Partial<Omit<SalesFormData, "password">>,
  ) {
    const { email, first_name, last_name, administrator, avatar, disabled } =
      data;

    const { data: updatedData, error } = await supabase.functions.invoke<{
      data: Sale;
    }>("users", {
      method: "PATCH",
      body: {
        sales_id: id,
        email,
        first_name,
        last_name,
        administrator,
        disabled,
        avatar,
      },
    });

    if (!updatedData || error) {
      console.error("salesCreate.error", error);
      throw new Error("Failed to update account manager");
    }

    return updatedData.data;
  },
  async updatePassword(id: Identifier) {
    const { data: passwordUpdated, error } =
      await supabase.functions.invoke<boolean>("update_password", {
        method: "PATCH",
        body: {
          sales_id: id,
        },
      });

    if (!passwordUpdated || error) {
      console.error("update_password.error", error);
      throw new Error("Failed to update password");
    }

    return passwordUpdated;
  },
  async unarchiveDeal(deal: Deal) {
    // get all deals where stage is the same as the deal to unarchive
    const { data: deals } = await baseDataProvider.getList<Deal>("deals", {
      filter: { stage: deal.stage },
      pagination: { page: 1, perPage: 1000 },
      sort: { field: "index", order: "ASC" },
    });

    // set index for each deal starting from 1, if the deal to unarchive is found, set its index to the last one
    const updatedDeals = deals.map((d, index) => ({
      ...d,
      index: d.id === deal.id ? 0 : index + 1,
      archived_at: d.id === deal.id ? null : d.archived_at,
    }));

    return await Promise.all(
      updatedDeals.map((updatedDeal) =>
        baseDataProvider.update("deals", {
          id: updatedDeal.id,
          data: updatedDeal,
          previousData: deals.find((d) => d.id === updatedDeal.id),
        }),
      ),
    );
  },
  async getActivityLog(companyId?: Identifier) {
    return getActivityLog(baseDataProvider, companyId);
  },
  async isInitialized() {
    return getIsInitialized();
  },
  async mergeContacts(sourceId: Identifier, targetId: Identifier) {
    const { data, error } = await supabase.functions.invoke("merge_contacts", {
      method: "POST",
      body: { loserId: sourceId, winnerId: targetId },
    });

    if (error) {
      console.error("merge_contacts.error", error);
      throw new Error("Failed to merge contacts");
    }

    return data;
  },
} satisfies DataProvider;

export type CrmDataProvider = typeof dataProviderWithCustomMethods;

const DEAL_CATEGORY_LABELS: Record<string, string> = {
  "free-pilot": "Free Pilot",
  "paid-pilot": "Paid Pilot",
  commercial: "Commercial",
};

function buildDealChangeLog(
  prev: Partial<Deal>,
  next: Partial<Deal>,
): string[] {
  const lines: string[] = [];

  const track = (
    label: string,
    field: keyof Deal,
    fmt?: (v: unknown) => string,
  ) => {
    // Only compare fields present in the update data (partial updates
    // omit unchanged fields — treating missing as "changed" is wrong)
    if (!(field in next)) return;
    const oldVal = prev[field];
    const newVal = next[field];
    if (oldVal === newVal) return;
    // Skip null/undefined/empty-string ↔ null/undefined/empty-string
    if (!oldVal && !newVal) return;
    const format = fmt ?? String;
    lines.push(`${label}: ${format(oldVal)} → ${format(newVal)}`);
  };

  const fmtCategory = (v: unknown) =>
    DEAL_CATEGORY_LABELS[String(v)] ?? String(v ?? "—");
  const fmtRM = (v: unknown) =>
    v ? `RM ${Number(v).toLocaleString()}` : "RM 0";
  const fmtRate = (v: unknown) =>
    `RM ${Number(v ?? 0)}/unit/month`;
  const fmtMonths = (v: unknown) =>
    `${v ?? "—"} months`;
  const fmtDate = (v: unknown) =>
    v ? String(v).split("T")[0] : "—";
  const fmtEntities = (v: unknown) => {
    const ids = v as number[] | undefined;
    return ids?.length ? `${ids.length} entities` : "none";
  };

  track("Category", "deal_category", fmtCategory);
  // Stage tracked separately by persistStageChange with labels
  track("Value", "amount", fmtRM);
  track("Rate", "rate_per_unit", fmtRate);
  track("Duration", "duration_months", fmtMonths);
  track("Closing date", "expected_closing_date", fmtDate);
  track("Discount", "discount", (v) => `${v ?? 0}%`);
  track("Payment terms", "payment_terms", (v) => String(v || "—"));
  track("Account Manager", "sales_id");

  // Entity changes — compare arrays (only if entity_ids in update)
  if ("entity_ids" in next) {
    const oldIds = (prev.entity_ids ?? []).map(Number).sort();
    const newIds = (next.entity_ids ?? []).map(Number).sort();
    if (JSON.stringify(oldIds) !== JSON.stringify(newIds)) {
      lines.push(`Entities: ${fmtEntities(oldIds)} → ${fmtEntities(newIds)}`);
    }
  }

  // Archive/unarchive
  if ("archived_at" in next) {
    if (!prev.archived_at && next.archived_at) {
      lines.push("Archived");
    } else if (prev.archived_at && !next.archived_at) {
      lines.push("Unarchived");
    }
  }

  // Won
  if ("won_at" in next && !prev.won_at && next.won_at) {
    lines.push(`Won on ${fmtDate(next.won_at)}`);
  }

  return lines;
}

const lifeCycleCallbacks: ResourceCallbacks[] = [
  {
    resource: "contact_notes",
    beforeSave: async (data: ContactNote, _, __) => {
      if (data.attachments) {
        data.attachments = await Promise.all(
          data.attachments.map((fi) => uploadToBucket(fi)),
        );
      }
      return data;
    },
  },
  {
    resource: "deal_notes",
    beforeSave: async (data: DealNote, _, __) => {
      if (data.attachments) {
        data.attachments = await Promise.all(
          data.attachments.map((fi) => uploadToBucket(fi)),
        );
      }
      return data;
    },
  },
  {
    resource: "sales",
    beforeSave: async (data: Sale, _, __) => {
      if (data.avatar) {
        await uploadToBucket(data.avatar);
      }
      return data;
    },
  },
  {
    resource: "contacts",
    beforeGetList: async (params) => {
      return applyFullTextSearch([
        "first_name",
        "last_name",
        "company_name",
        "title",
        "email",
        "phone",
        "background",
      ])(params);
    },
  },
  {
    resource: "companies",
    beforeGetList: async (params) => {
      return applyFullTextSearch([
        "name",
        "phone_number",
        "website",
        "zipcode",
        "city",
        "state_abbr",
      ])(params);
    },
    beforeCreate: async (params) => {
      const createParams = await processCompanyLogo(params);

      return {
        ...createParams,
        data: {
          created_at: new Date().toISOString(),
          ...createParams.data,
        },
      };
    },
    beforeUpdate: async (params) => {
      return await processCompanyLogo(params);
    },
  },
  {
    resource: "contacts_summary",
    beforeGetList: async (params) => {
      return applyFullTextSearch(["first_name", "last_name"])(params);
    },
  },
  {
    resource: "deals",
    beforeGetList: async (params) => {
      return applyFullTextSearch(["name", "description"])(params);
    },
    afterUpdate: async (result) => {
      // Change tracking runs via beforeUpdate — afterUpdate just
      // passes through the result
      return result;
    },
    beforeUpdate: async (params) => {
      const prev = params.previousData as Deal | undefined;
      const next = params.data as Partial<Deal>;
      const dealId = params.id ?? next.id ?? prev?.id;
      if (!prev || !dealId) return params;

      try {
        const changes = buildDealChangeLog(prev, next);
        if (changes.length > 0) {
          const { data: { user } } = await supabase.auth.getUser();
          let salesId: Identifier | null = null;
          if (user) {
            const { data: sale } = await supabase
              .from("sales")
              .select("id")
              .eq("user_id", user.id)
              .single();
            salesId = sale?.id ?? null;
          }

          await supabase.from("deal_notes").insert({
            deal_id: dealId,
            text: `Deal updated\n${changes.join("\n")}`,
            date: new Date().toISOString(),
            sales_id: salesId,
          });
        }
      } catch (err) {
        console.warn("Deal change tracking failed:", err);
      }

      return params;
    },
  },
  {
    resource: "entities_summary",
    beforeGetList: async (params) => {
      return applyFullTextSearch(["name", "company_name"])(params);
    },
  },
];

export const dataProvider = withLifecycleCallbacks(
  dataProviderWithCustomMethods,
  lifeCycleCallbacks,
);

export const applyFullTextSearch = (columns: string[]) => (params: GetListParams) => {
  if (!params.filter?.q) {
    return params;
  }
  const { q, ...filter } = params.filter;
  return {
    ...params,
    filter: {
      ...filter,
      "@or": columns.reduce((acc, column) => {
        if (column === "email")
          return {
            ...acc,
            [`email_fts@ilike`]: q,
          };
        if (column === "phone")
          return {
            ...acc,
            [`phone_fts@ilike`]: q,
          };
        else
          return {
            ...acc,
            [`${column}@ilike`]: q,
          };
      }, {}),
    },
  };
};

const uploadToBucket = async (fi: RAFile) => {
  if (!fi.src.startsWith("blob:") && !fi.src.startsWith("data:")) {
    // Sign URL check if path exists in the bucket
    if (fi.path) {
      const { error } = await supabase.storage
        .from("attachments")
        .createSignedUrl(fi.path, 60);

      if (!error) {
        return fi;
      }
    }
  }

  const dataContent = fi.src
    ? await fetch(fi.src)
        .then((res) => {
          if (res.status !== 200) {
            return null;
          }
          return res.blob();
        })
        .catch(() => null)
    : fi.rawFile;

  if (dataContent == null) {
    // We weren't able to download the file from its src (e.g. user must be signed in on another website to access it)
    // or the file has no content (not probable)
    // In that case, just return it as is: when trying to download it, users should be redirected to the other website
    // and see they need to be signed in. It will then be their responsibility to upload the file back to the note.
    return fi;
  }

  const file = fi.rawFile;
  const fileParts = file.name.split(".");
  const fileExt = fileParts.length > 1 ? `.${file.name.split(".").pop()}` : "";
  const fileName = `${Math.random()}${fileExt}`;
  const filePath = `${fileName}`;
  const { error: uploadError } = await supabase.storage
    .from("attachments")
    .upload(filePath, dataContent);

  if (uploadError) {
    console.error("uploadError", uploadError);
    throw new Error("Failed to upload attachment");
  }

  const { data } = supabase.storage.from("attachments").getPublicUrl(filePath);

  fi.path = filePath;
  fi.src = data.publicUrl;

  // save MIME type
  const mimeType = file.type;
  fi.type = mimeType;

  return fi;
};
